/**
 *
 * @module httpClient
 *
 */

/* eslint-disable consistent-return */
/* eslint-disable guard-for-in */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Configuration from './../../configuration/entry';
import Exception from './../../exception/entry';
import CoreHttpClientProvider from './../shared/coreHttpClientProvider';

const HttpMethod = Configuration.HttpMethod;
const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;
const ErrorReason = Exception.ErrorReason;
const ExceptionReference = Exception.ExceptionReference;

const {
    like
} = Check.assert;

/**
 *
 * @class HttpClient
 * @extends {CoreHttpClientProvider}
 * @desc Apple ATV Legacy HttpClient implementation.
 *
 * @example
 * const httpClient = new HttpClient(logger);
 *
 */
export default class HttpClient extends CoreHttpClientProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(logger = Logging) {

        super(logger);

        this.logger.log(HttpClient.name, 'Created ATV Provider.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.post(options).then((response) => { console.log(response.data); });
     *
     */
    post(options) {

        const errorMessage = {
            method: 'HttpClient.post(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return this.makeRequest(HttpMethod.POST, options).then((response) => {

            return response;

        }).catch((ex) => {

            return Promise.reject(ex);

        });

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.get(options).then((response) => { console.log(response.data); });
     *
     */
    get(options) {

        const errorMessage = {
            method: 'HttpClient.get(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return this.makeRequest(HttpMethod.GET, options).then((response) => {

            return response;

        }).catch((ex) => {

            return Promise.reject(ex);

        });

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @returns {Promise<Void>}
     *
     * @example
     * httpClient.del(options).then((response) => { console.log(response.data); });
     *
     */
    del(options) {

        const errorMessage = {
            method: 'HttpClient.del(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return this.makeRequest(HttpMethod.DELETE, options).then((response) => {

            return response;

        }).catch((ex) => {

            return Promise.reject(ex);

        });

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @returns {Promise<Void>}
     *
     * @example
     * httpClient.patch(options).then((response) => { console.log(response.data); });
     *
     */
    patch(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return this.makeRequest(HttpMethod.PATCH, options).then((response) => {

            return response;

        }).catch((ex) => {

            return Promise.reject(ex);

        });

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.put(options).then((response) => { console.log(response.data); });
     *
     */
    put(options) {

        const errorMessage = {
            method: 'HttpClient.put(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return this.makeRequest(HttpMethod.PUT, options).then((response) => {

            return response;

        }).catch((ex) => {

            return Promise.reject(ex);

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} method
     * @param {Object} options
     * @desc constructs request based on provided HTTP method (i.e. GET, POST PUT, PATCH, DELETE),
     * if the response cannot be parsed, provide the response as a plain String
     * @throws {NetworkException}
     * @returns {Promise<Void>}
     *
     */
    makeRequest(method, options) {

        return new Promise((resolve, reject) => {

            const { getResponseData, onError } = this;
            const { url, headers, body, bodyType } = options;

            const hasArrayBufferType = Check.equal(bodyType, 'arrayBuffer');
            const xhr = new XMLHttpRequest();

            /**
             *
             * @method onreadystatechange
             * @returns {Promise<Object>}
             * @note executed each time readyState changes
             * @note readyState of 4 is the equivalent of `XMLHttpRequest.DONE`
             *
             */
            xhr.onreadystatechange = () => {

                const { parseHeaders, getHeaderWithKey } = this;
                const { status, statusText, responseText, readyState, responseDataAsBase64 } = xhr;

                if (readyState === 4) {

                    if (status) {

                        const responseHeaders = parseHeaders(xhr);

                        return resolve({
                            status,
                            data: hasArrayBufferType ? responseDataAsBase64 : getResponseData(responseText),
                            headers: {
                                get: (key) => {
                                    return getHeaderWithKey(key, responseHeaders);
                                }
                            }
                        });

                    } else {

                        const reasons = [new ErrorReason(status, statusText)];
                        const exceptionData = ExceptionReference.common.network;

                        return reject(new ServiceException({ reasons, exceptionData }));

                    }

                }

            };

            /**
             *
             * @method onerror
             * @returns {Promise<NetworkException>}
             *
             */
            xhr.onerror = onError;

            try {

                xhr.open(method, url);

                for (const headerKey in headers) {
                    xhr.setRequestHeader(headerKey, headers[headerKey]);
                }

            } catch (error) {

                const reasons = [new ErrorReason(error.name, error.message)];
                const exceptionData = ExceptionReference.common.network;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

            try {

                xhr.send(body);

            } catch (error) {

                const reasons = [new ErrorReason(error.name, error.message)];
                const exceptionData = ExceptionReference.common.network;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access private
     * @param {Object} [request={}]
     * @desc retrieves then parses response headers into an object
     * @returns {Object}
     *
     */
    parseHeaders(request = {}) {

        const canGetHeaders = Check.function(request.getAllResponseHeaders);

        const headerStr = canGetHeaders ? request.getAllResponseHeaders() : '';
        const headerArr = headerStr.trim().split(/[\r\n]+/);
        const headers = {};

        headerArr.forEach((line) => {

            const parts = line.split(': ');
            const header = parts.shift();
            const value = parts.join(': ');

            headers[header] = value;

        });

        return headers;

    }

    /**
     *
     * @access private
     * @method getHeaderWithKey
     * @param {String} [key='']
     * @param {Object} [headers={}]
     * @returns {String|Null}
     *
     */
    getHeaderWithKey(key = '', headers = {}) {

        for (const keys in headers) {
            if (keys.toLowerCase() === key.toLowerCase()) {
                return headers[key];
            }
        }

        return null;

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/apple/httpClient.js